import { mapGetters } from 'vuex'
import chargingpointCpoMixin from '@/mixins/chargingpoint/chargingpointCpoMixin'
import { provincieCodeByMunicipalityCode } from '@/services/municipalities'
import {SOURCE, SOURCE_TYPE, getSourceLabel} from '@/services/sourceTranslation'
import { introducedBy } from '@/services/introductionSource'
import { getLatestCoordinates } from '@/helpers/chargingpoints'
import { STAKEHOLDER_TYPE } from '@/../shared/valueholders/stakeholder-types'

export default {
  filters: {
    formatTokW: (x) => {
      return `${Math.floor(x / 1000)} kW`
    },
  },
  mixins: [chargingpointCpoMixin],
  data() {
    return {
      networkOptions: [],
      ownerOptions: [{ value: null, text: this.$i18n.t('none') }],
      faseOptions: [{
        value: 1,
        label: 'Fase 1',
      }, {
        value: 2,
        label: 'Fase 2',
      }],
    }
  },
  computed: {
    ...mapGetters('planmode', {
      chargingpoint: 'getSelectedChargingpoint',
    }),
    ...mapGetters('access', [
      'getActiveMunicipality',
      'getPrivateNetworks',
      'getOwners'
    ]),
    ...mapGetters('config', ['getPhases', 'getConfigByCode']),
    config() {
      return this.getConfigByCode({ code: this.code })
    },
    isBraLiMunicipality() {
      return ([31, 30].includes(provincieCodeByMunicipalityCode({ code: this.getActiveMunicipality })))
    },
    ...mapGetters('currentUser', {
      currentUserRole: 'getCurrentUserRole',
    }),
    code() {
      return this.chargingpoint.code
    },
    id() {
      return this.chargingpoint.properties.id
    },
    address() {
      return this.chargingpoint.address
    },
    addressSource() {
      const source = this.chargingpoint?.dataSource?.value
      const sourceType = this.chargingpoint?.meta?.sourceType

      if (sourceType === SOURCE_TYPE.DASHBOARD) {
        const importConfig = this.config?.imports?.find(i => i.addressSource === source)
        return importConfig?.addressSourceLabel
      }

      return getSourceLabel(source)
    },
    isAddressEditable() {
      // only NONE ecomovement chargingpoints are enabled for editing the address
      return this.chargingpoint?.dataSource.value !== SOURCE.ECO_MOVEMENT
    },
    hasAddress() {
      return !!this.chargingpoint.address
    },
    isAddressComplete() {
      return !!(
        this.chargingpoint.address?.street &&
        this.chargingpoint.address?.number &&
        this.chargingpoint.address?.postalcode &&
        this.chargingpoint.address?.city
      )
    },
    evse() {
      return this.chargingpoint.properties.evse
    },
    evse_ids() {
      return this.chargingpoint.evse_ids || []
    },
    importedWithoutCoordinates() {
      return this.chargingpoint.meta?.hadCoordinates === false ?? false
    },
    coordinates() {
      // if the chargingpoint was merged, the coordinates are stored in merged_attributes. Use these coordinates if available
      const coordinates = getLatestCoordinates({ chargingpoint: this.chargingpoint })

      if (!coordinates[0] || !coordinates[1]) {
        return false
      }

      return {
        'lng': coordinates[0],
        'lat': coordinates[1],
      }
    },
    prio() {
      return this.chargingpoint.prio
    },
    customPhase() {
      return this.getPhases.find(phase => phase.uuid === this.prio?.customPhase)?.label
    },
    trafficDecision() {
      return this.chargingpoint.trafficDecision
    },
    introducedBy() {
      return introducedBy(this.chargingpoint, this.config)
    },
    remark() {
      return this.chargingpoint.properties.remark ?? ''
    },
    stakeholders() {
      return this.chargingpoint.properties.stakeholders ?? []
    },
    status() {
      return this.chargingpoint.properties.status
    },
    user() {
      return this.chargingpoint.properties.user
    },
    isUserCpo() {
      return this.currentUserRole === 'cpo'
    },
    identity() {
      return this.$auth.user['https://evmaps.nl/identity']
    },
    types() {
      return this.chargingpoint.properties.type?.split('/')
    },
    power() {
      return this.chargingpoint.properties.power
    },
    nroutlets() {
      return this.chargingpoint.properties.nroutlets
    },
    owner() {
      return this.stakeholders.find(stakeholder => stakeholder.category === STAKEHOLDER_TYPE.OWNER)
    },
    beheerder() {
      return this.chargingpoint.properties.beheerder
    },
    locationUUID() {
      return this.chargingpoint.uuid
    },
    validators() {
      return this.chargingpoint.properties.validators
    },
    predecessor() {
      return this.chargingpoint.properties.predecessor
    },
    hasValidators() {
      return this.validators?.length > 0
    },
    statusLabel() {
      return this.status ?? 'definitive'
    },
    closingDates() {
      return this.chargingpoint.participation?.closingDates
    },
    publish() {
      return this.chargingpoint.publish
    },
    publish_allowed_to() {
      return this.chargingpoint.publish_allowed_to
    },
    images(){
      return this.chargingpoint.images
    },
    canUseB3Phase() {
      return !this.identity?.uuid || !this.isUserCpo || this.identity?.name === 'Vattenfall'
    },
    isBoundToProcess() {
      return !!this.chargingpoint.workflowCaseRef ||
        !!this.chargingpoint.workflowUuid ||
        !!this.chargingpoint.externalProcessId ||
        !!this.chargingpoint.isLockedForEditing
    },
  },
  created() {
    this.getPhases
      // Exclude phase B3 if user is Cpo and not Vattenfall
      .filter(phase => phase.active && (!this.canUseB3Phase ? !phase.label.includes('B3') : true))
      .map(phase => this.faseOptions.push({ value: phase.uuid, label: phase.label }))

    const networks = this.getPrivateNetworks

    networks.map(({ name, uuid }) => this.networkOptions.push({
      label: name,
      value: uuid,
    }))

    const owners = this.getOwners
    owners.map(({ name, uuid }) => this.ownerOptions.push({
      text: name,
      value: uuid,
    }))
  },
}
